import { useEffect, useRef } from 'react'
import type { Frame } from '../../../shared/types'
import { usePageCanvas } from '../../lib/pages'
import { useHeatmaps, type HeatmapData } from './heatmaps'
import { reachLine } from './reach'
import { useT } from '@/lib/i18n'

/** How far one click spreads, in frame px. */
const RADIUS = 22
/** Canvas backing stores beyond this many pixels fail silently in some
 *  engines; a taller page paints at a lower density instead. */
const MAX_PIXELS = 16_000_000
const REACH_SHARES = [0.75, 0.5, 0.25]

/* cold to hot, the palette heatmaps are read in everywhere */
const STOPS: [number, [number, number, number]][] = [
  [0, [37, 99, 235]],
  [0.35, [6, 182, 212]],
  [0.55, [34, 197, 94]],
  [0.75, [250, 204, 21]],
  [1, [239, 68, 68]],
]

function colourAt(t: number): [number, number, number] {
  for (let i = 1; i < STOPS.length; i++) {
    const [end, to] = STOPS[i]!
    const [start, from] = STOPS[i - 1]!
    if (t <= end) {
      const k = (t - start) / (end - start)
      return [0, 1, 2].map((c) => Math.round(from[c]! + (to[c]! - from[c]!) * k)) as [number, number, number]
    }
  }
  return STOPS.at(-1)![1]
}

const PALETTE = Array.from({ length: 256 }, (_, i) => colourAt(i / 255))

/** Paint density as alpha, then colour each pixel by how dense it is. */
function paint(canvas: HTMLCanvasElement, frame: Frame, data: HeatmapData) {
  const scale = Math.min(1, Math.sqrt(MAX_PIXELS / Math.max(1, frame.width * frame.height)))
  canvas.width = Math.max(1, Math.round(frame.width * scale))
  canvas.height = Math.max(1, Math.round(frame.height * scale))
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx || data.points.length === 0) return
  const max = Math.max(...data.points.map((point) => point.n))
  const radius = RADIUS * scale
  for (const point of data.points) {
    const x = point.x * scale
    const y = point.y * scale
    if (y - radius > canvas.height || x - radius > canvas.width) continue
    const glow = ctx.createRadialGradient(x, y, 0, x, y, radius)
    /* a square root keeps a single stray click visible beside a hot spot */
    glow.addColorStop(0, `rgba(0,0,0,${Math.max(0.12, Math.sqrt(point.n / max))})`)
    glow.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = glow
    ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2)
  }
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const pixels = image.data
  for (let i = 0; i < pixels.length; i += 4) {
    const alpha = pixels[i + 3]!
    if (alpha === 0) continue
    const [r, g, b] = PALETTE[alpha]!
    pixels[i] = r
    pixels[i + 1] = g
    pixels[i + 2] = b
    pixels[i + 3] = Math.min(220, alpha + 60)
  }
  ctx.putImageData(image, 0, 0)
}

function FrameHeatmap({ frame, data }: { frame: Frame; data: HeatmapData }) {
  const ref = useRef<HTMLCanvasElement>(null)
  const t = useT()
  useEffect(() => {
    if (ref.current) paint(ref.current, frame, data)
  }, [frame, data])
  const total = data.points.reduce((sum, point) => sum + point.n, 0)
  const lines = REACH_SHARES.map((share) => ({ share, y: reachLine(data.reach, share) })).filter(
    (line): line is { share: number; y: number } => line.y !== undefined && line.y < frame.height,
  )
  return (
    <div
      className="pointer-events-none absolute"
      style={{ left: frame.x, top: frame.y, width: frame.width, height: frame.height }}
      data-posthog-heatmap={frame.id}
    >
      <canvas ref={ref} className="absolute inset-0 size-full" />
      {lines.map(({ share, y }) => (
        <div key={share} className="absolute inset-x-0 border-t border-dashed border-ink/60" style={{ top: y }}>
          <span
            className="absolute right-0 top-0 origin-top-right rounded-tag bg-ink px-1.5 py-0.5 text-micro text-paper"
            style={{ scale: 'calc(1 / var(--zoom, 1))' }}
          >
            {t('stage.posthogReadThisFar', { percent: Math.round(share * 100) })}
          </span>
        </div>
      ))}
      <span
        className="absolute left-0 top-0 origin-top-left rounded-tag bg-ink px-1.5 py-0.5 text-micro text-paper"
        style={{ scale: 'calc(1 / var(--zoom, 1))' }}
      >
        {t(data.type === 'click' ? 'stage.posthogClicksCaption' : 'stage.posthogRageCaption', {
          count: total,
          days: data.days,
        })}
      </span>
    </div>
  )
}

/** The heatmaps people turned on, laid over their frames on this page. */
export function HeatmapOverlay() {
  const canvas = usePageCanvas()
  const frames = useHeatmaps((s) => s.frames)
  if (!canvas) return null
  return (
    <>
      {canvas.frames.map((frame) => {
        const data = frames[frame.id]?.data
        return data && !frame.hidden ? <FrameHeatmap key={frame.id} frame={frame} data={data} /> : null
      })}
    </>
  )
}
